import React, { useState } from "react";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskPanel,
  DeskPill,
  DeskRowMenu,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import { formatLogTimestamp } from "../timelogs/timeLogModel";
import { formatDuration } from "../../helpers/tasks/taskDuration";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";

const shortDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Desktop Task Catalog: master list + contextual preview panel.
 * Same data and actions as the phone list; a catalog item is a definition, so
 * there is no completion state here.
 */
export function TaskCatalogDesktop({
  onBack,
  items,
  shown,
  loading,
  error,
  canEdit,
  scope,
  onScopeChange,
  search,
  onSearch,
  onNew,
  onOpen,
  onEdit,
  onToggleActive,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const { minutesPerDay } = useWorkSchedule();
  const selected = shown.find((item) => item.task_catalog_id === selectedId) || null;
  const active = items.filter((i) => i.is_active !== false).length;
  const archived = items.length - active;

  const rowMenu = (item) =>
    canEdit
      ? [
          { key: "edit", label: "Edit", onSelect: () => onEdit(item) },
          {
            key: "toggle",
            label: item.is_active === false ? "Restore" : "Archive",
            danger: item.is_active !== false,
            onSelect: () => onToggleActive(item),
          },
        ]
      : [];

  const columns = [
    {
      key: "title",
      label: "Task",
      width: "28%",
      primary: true,
      render: (item) => <span className="dt-strong">{item.title}</span>,
    },
    {
      key: "desc",
      label: "Default Instructions",
      width: "28%",
      render: (item) => (
        <span className="dt-muted dt-clamp">{item.description || "No default instructions"}</span>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      width: "10%",
      render: (item) => <span className="dt-muted dt-nowrap">{formatDuration(item.planned_duration_minutes, minutesPerDay) || "—"}</span>,
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      render: (item) =>
        item.is_active === false ? <DeskPill tone="muted">Archived</DeskPill> : <DeskPill tone="green">Active</DeskPill>,
    },
    {
      key: "updated",
      label: "Updated",
      width: "16%",
      render: (item) => <span className="dt-muted dt-nowrap">{shortDate(item.updated_at || item.created_at)}</span>,
    },
    {
      key: "actions",
      label: "",
      className: "dt-cell--actions",
      width: "56px",
      render: (item) => <DeskRowMenu label={`Actions for ${item.title}`} items={rowMenu(item)} onSelect={(m) => m.onSelect()} />,
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Tasks"
      subtitle="Reusable work definitions"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch value={search} onChange={onSearch} placeholder="Search tasks…" ariaLabel="Search tasks" />
          {canEdit ? (
            <HubPrimaryAction onClick={onNew} label="New Task" ariaLabel="+ New Task" />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Task catalog summary"
        tiles={[
          { key: "total", value: String(items.length), label: "Catalog Tasks", tone: "gold" },
          { key: "active", value: String(active), label: "Active", tone: "green" },
          { key: "archived", value: String(archived), label: "Archived", tone: "muted" },
        ]}
      />

      <DeskToolbar>
        <DeskTabs
          ariaLabel="Task catalog status"
          options={[
            { value: "active", label: "Active", count: active },
            { value: "archived", label: "Archived", count: archived },
            { value: "all", label: "All", count: items.length },
          ]}
          value={scope}
          onChange={onScopeChange}
        />
        <span className="dt-toolbar__grow" />
        <span className="dt-summary">
          {shown.length} {shown.length === 1 ? "task" : "tasks"}
        </span>
      </DeskToolbar>

      {error ? (
        <div className="rec-banner" role="alert" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <DeskEmpty title="Loading…" />
      ) : shown.length === 0 ? (
        <DeskEmpty title={items.length === 0 ? "No catalog tasks yet" : "No tasks match"}>
          {items.length === 0
            ? canEdit
              ? "Add the work you repeat on projects."
              : "No catalog tasks yet."
            : "Try a different search or status."}
        </DeskEmpty>
      ) : (
        <div className="dp-grid dp-grid--main-side">
          <DeskTable
            testId="catalog-table"
            caption="Task catalog"
            columns={columns}
            rows={shown}
            rowKey={(item) => item.task_catalog_id}
            onOpen={(item) => setSelectedId(item.task_catalog_id)}
            openLabel={(item) => `Preview ${item.title}`}
            rowClassName={(item) =>
              `${item.task_catalog_id === selectedId ? "is-selected " : ""}${item.is_active === false ? "is-muted" : ""}`
            }
          />
          <aside className="dp-sticky" aria-label="Task preview">
            {selected ? (
              <DeskPanel
                title={selected.title}
                actions={
                  selected.is_active === false ? (
                    <DeskPill tone="muted">Archived</DeskPill>
                  ) : (
                    <DeskPill tone="green">Active</DeskPill>
                  )
                }
              >
                <h3 className="dp-label">Default instructions</h3>
                <p className="dp-text">{selected.description || "No default instructions."}</p>
                <p className="dp-hint">
                  A reusable definition. Adding it to a project copies it into a separate Project Task, so
                  changing it here never changes work already on a project.
                </p>
                <dl className="dp-kv dp-kv--tight">
                  <dt>Planned duration</dt>
                  <dd>{formatDuration(selected.planned_duration_minutes, minutesPerDay) || "Not set"}</dd>
                  {selected.created_at ? (
                    <>
                      <dt>Created</dt>
                      <dd>{formatLogTimestamp(selected.created_at)}</dd>
                    </>
                  ) : null}
                  {selected.updated_at ? (
                    <>
                      <dt>Updated</dt>
                      <dd>{formatLogTimestamp(selected.updated_at)}</dd>
                    </>
                  ) : null}
                </dl>
                <div className="dp-actions">
                  <button type="button" className="dt-btn" onClick={() => onOpen(selected)}>
                    Open details
                  </button>
                  {canEdit ? (
                    <>
                      <button type="button" className="dt-btn" onClick={() => onEdit(selected)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`dt-btn${selected.is_active === false ? "" : " dt-btn--danger"}`}
                        onClick={() => onToggleActive(selected)}
                      >
                        {selected.is_active === false ? "Restore" : "Archive"}
                      </button>
                    </>
                  ) : null}
                </div>
              </DeskPanel>
            ) : (
              <DeskPanel>
                <p className="dp-hint">Select a task to preview its default instructions.</p>
              </DeskPanel>
            )}
          </aside>
        </div>
      )}
    </CrewPilotModuleShell>
  );
}
